import type {
  RoleCategory,
  RoleParty,
  RoleResponsibilityRow,
  RolesResponsibilitiesContent
} from '@/blocks/domain/types/BlockContent/RolesResponsibilitiesContent';

type LegacyRow = { id?: string; role: string; responsibility: string; hidden?: boolean };

function partyFromRole(role: string): RoleParty {
  const normalized = role.trim().toLowerCase();
  if (normalized === 'customer' || normalized === 'client') return 'customer';
  if (normalized === 'company' || normalized === 'provider') return 'company';
  return 'shared';
}

/**
 * The text of a legacy row's party when it names none of the three parties ("Raptor"), so it can
 * ride along instead of silently turning into "Shared". "Shared" itself is already what a shared
 * row prints, and a blank party has no meaning to keep.
 */
function legacyPartyLabel(role: string): string | undefined {
  const normalized = role.trim().toLowerCase();
  if (normalized === '' || normalized === 'shared') return undefined;
  return partyFromRole(role) === 'shared' ? role : undefined;
}

/** The free-text party a row prints in place of its party's label, if it has one. Only a shared row carries it. */
export function customPartyLabel(row: Pick<RoleResponsibilityRow, 'party' | 'partyLabel'>): string | undefined {
  return row.party === 'shared' && row.partyLabel !== undefined && row.partyLabel.trim() !== '' ? row.partyLabel : undefined;
}

/** Deterministic ids. Hosts key hidden-sets on these values, so they must not use the clock. */
export function toRoleCategories(rows: readonly LegacyRow[]): RoleCategory[] {
  return rows.map((row, index) => {
    const partyLabel = legacyPartyLabel(row.role);
    return {
      id: `cat-legacy-${index}`,
      name: row.role,
      rows: [
        {
          id: row.id ?? `row-${index}`,
          text: row.responsibility,
          party: partyFromRole(row.role),
          ...(partyLabel === undefined ? {} : { partyLabel }),
          ...(row.hidden === true ? { hidden: true } : {})
        }
      ]
    };
  });
}

export function toLegacyRoleRows(categories: readonly RoleCategory[]): LegacyRow[] {
  return categories.flatMap((category) =>
    category.rows.map((row) => ({
      id: row.id,
      role: category.name,
      responsibility: row.text
    }))
  );
}

/**
 * The label each party is mirrored under in `rows[].role`. The CRM template seed classifies a row
 * by looking for "customer" / "shared" in that label and treats everything else as the provider,
 * so these three words are the contract: do not reword them without changing that classifier.
 */
const MIRROR_ROLE_BY_PARTY: Record<RoleParty, string> = {
  customer: 'Customer',
  company: 'Company',
  shared: 'Shared'
};

/**
 * The legacy `rows` that sit beside `categories` for consumers that only read `rows` (the CRM
 * template seed). Unlike `toLegacyRoleRows`, `role` carries the row's party, not its group's
 * subheading: the subheading is free text the seed would misclassify as a provider duty. A row
 * that kept a free-text party from its legacy form ("Raptor") writes that text back unchanged, so
 * the seed classifies it exactly as it did before the block was edited. Rows with no
 * responsibility text are left out, as the seed ignores them anyway.
 */
export function toRoleRowsMirror(categories: readonly RoleCategory[]): LegacyRow[] {
  return categories.flatMap((category) =>
    category.rows
      .filter((row) => row.text.trim() !== '')
      .map((row) => ({
        id: row.id,
        role: customPartyLabel(row) ?? MIRROR_ROLE_BY_PARTY[row.party],
        responsibility: row.text,
        ...(row.hidden === true ? { hidden: true } : {})
      }))
  );
}

export function rolesUseCategories(content: Pick<RolesResponsibilitiesContent, 'categories'>): boolean {
  return Array.isArray(content.categories) && content.categories.length > 0;
}
